// client/extensions/Collab.ts
import { A } from "@adornis/base/env-info.js";
import { commitCollabPacket } from "@adornis/collab/api/commitCollabPacket.js";
import { ensureCollab } from "@adornis/collab/api/ensureCollab.js";
import { subscribeCollabPacket } from "@adornis/collab/api/subscribeCollabPacket.js";
import { CollabPacket, CollabPacketType } from "@adornis/collab/db/CollabPacket.js";
import { currentUser } from "@adornis/users/client/currentUser.js";
import { Extension, Mark, mergeAttributes } from "@tiptap/core";
import { keymap } from "prosemirror-keymap";
import { BehaviorSubject, Subject } from "rxjs";
import { bufferTime, takeUntil } from "rxjs/operators";
import { redo, undo, yCursorPlugin, ySyncPlugin, ySyncPluginKey, yUndoPlugin } from "y-prosemirror";
import * as awarenessProtocol from "y-protocols/awareness";
import * as yjs from "yjs";
async function bufferToBase64(buffer) {
  const base64url = await new Promise((r) => {
    const reader = new FileReader();
    reader.onload = () => r(reader.result);
    reader.readAsDataURL(new Blob([buffer]));
  });
  return base64url.slice(base64url.indexOf(",") + 1);
}
var calcYChangeStyle = (args) => {
  switch (args.ychange_type) {
    case "removed":
      return `color: red`;
    case "added":
      return `background-color: ${args.ychange_color}`;
    case null:
      return "";
  }
};
var hoverWrapper = (args, els) => args === null ? els : [
  ["span", { class: "ychange-hover", style: `background-color:${args.ychange_color}` }, args.ychange_user],
  ["span", ...els]
];
var yChangeMark = Mark.create({
  name: "ychange",
  inclusive: false,
  addAttributes() {
    return {
      user: {
        default: null,
        renderHTML: (attributes) => {
          return {
            ychange_user: `${attributes.user ?? "Unknown"}`
          };
        }
      },
      type: {
        default: null,
        renderHTML: (attributes) => {
          return {
            ychange_type: `${attributes.type}`
          };
        }
      },
      color: {
        default: null,
        renderHTML: (attributes) => {
          return {
            ychange_color: `${attributes.color.light}`
          };
        }
      }
    };
  },
  parseHTML() {
    return [{ tag: "ychange" }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "ychange",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        style: calcYChangeStyle(HTMLAttributes)
      }),
      ...hoverWrapper(HTMLAttributes, [0])
    ];
  }
});
var colorPallet = [
  "#CAF4FF",
  "#BACD92",
  "#F5DAD2",
  "#FFB38E",
  "#E178C5",
  "#C5EBAA",
  "#A5DD9B",
  "#BEADFA",
  "#D7C0AE",
  "#9BABB8",
  "#FFD966"
];
function getStableColorByUsername(username) {
  const sum = username.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return colorPallet[sum % colorPallet.length];
}
function setDefaultColorPallet(pallet) {
  colorPallet = pallet;
}
function getDefaultColorPallet() {
  return colorPallet;
}
function createCollabExtension(ent, roomID, options) {
  return createCollabExtensionByClass(ent._class, roomID, options);
}
function getAnonymousSteve() {
  return `Anonymous Steve ${Math.ceil(Math.random() * 999)}`;
}
function createCollabExtensionByClass(_class, roomID, options) {
  const until = new Subject();
  const subjectUserID = new Subject();
  const subjectUsername = new Subject();
  const doc = new yjs.Doc({ gc: false });
  const text = doc.get("prosemirror", yjs.XmlFragment);
  const awareness = new awarenessProtocol.Awareness(doc);
  const cid = doc.clientID.toFixed(0);
  const onDebug = (fn) => options?.debugLog ? fn() : void 0;
  const noUsername = getAnonymousSteve();
  let pud = null;
  let lastUpdate = null;
  let lastLocalState = null;
  let disableUpdates = true;
  let userID = null;
  let errors = new Subject();
  const sendAwarenessUpdate = () => {
    if (awareness.getLocalState() === null || options?.readonly) {
      return;
    }
    const buffer = awarenessProtocol.encodeAwarenessUpdate(awareness, [doc.clientID]);
    bufferToBase64(buffer).then((str) => {
      onDebug(() => console.log("[COLLAB EXTENSION] sending awareness update", str));
      commitCollabPacket(
        roomID,
        _class,
        userID ?? cid,
        awareness.getLocalState()?.user?.name,
        CollabPacketType.Awareness,
        str
      )().catch(console.error);
    }).catch(console.error);
  };
  const registerCollab = async () => {
    const ensure = await ensureCollab(roomID, _class)();
    onDebug(() => console.log("[COLLAB EXTENSION] ensure collab", ensure));
    if (!ensure) {
      throw new Error("collab could not be established");
    }
    awareness.setLocalStateField("user", {
      color: options?.userColor ?? getStableColorByUsername(noUsername),
      name: noUsername
    });
    if (options?.userProvider) {
      options.userProvider.pipe(takeUntil(until)).subscribe((user) => {
        let username = null;
        if (typeof user === "object") {
          username = user.username;
          userID = user.userID;
        } else {
          username = user;
          userID = null;
        }
        subjectUserID.next(userID);
        subjectUsername.next(username);
        onDebug(() => console.log("[COLLAB EXTENSION] custom user provider got a new user", user));
        if ((username ?? "").length === 0 || awareness.getLocalState()?.user?.name === username) {
          onDebug(
            () => console.log(
              "[COLLAB EXTENSION] user not found or already set",
              username,
              awareness.getLocalState()?.user?.name
            )
          );
          return;
        }
        if (!disableUpdates) {
          pud?.setUserMapping(doc, doc.clientID, username ?? noUsername);
        }
        awareness.setLocalStateField("user", {
          color: getStableColorByUsername(username ?? noUsername),
          name: username ?? noUsername
        });
      });
    } else {
      currentUser().pipe(takeUntil(until)).subscribe((user) => {
        userID = user?._id;
        if (user?.username.length === 0 || awareness.getLocalState()?.user?.name === user?.username) {
          onDebug(
            () => console.log(
              "[COLLAB EXTENSION] user not found or already set",
              user?.username,
              awareness.getLocalState()?.user?.name
            )
          );
          return;
        }
        subjectUserID.next(userID);
        subjectUsername.next(user?.username);
        if (!disableUpdates) {
          pud?.setUserMapping(doc, doc.clientID, user?.username ?? noUsername);
        }
        awareness.setLocalStateField("user", {
          color: getStableColorByUsername(user?.username ?? noUsername),
          name: user?.username ?? noUsername
        });
      });
    }
    subscribeCollabPacket(roomID)(CollabPacket.allFields).pipe(takeUntil(until)).subscribe(
      (packet) => {
        if (packet.cid === cid) {
          return;
        }
        switch (packet.type) {
          case CollabPacketType.Populate:
            disableUpdates = false;
            setTimeout(() => {
              pud = new yjs.PermanentUserData(doc);
              pud.setUserMapping(doc, doc.clientID, awareness.getLocalState()?.user.name ?? noUsername);
            }, 0);
          case CollabPacketType.Update:
            lastUpdate = packet.payload;
            yjs.applyUpdate(doc, packet.payloadUint8Array);
            break;
          case CollabPacketType.Awareness:
            awarenessProtocol.applyAwarenessUpdate(awareness, packet.payloadUint8Array, parseInt(packet.cid ?? "0"));
            awareness.emit("update", []);
            break;
          case CollabPacketType.QueryAwareness:
            sendAwarenessUpdate();
            break;
        }
        onDebug(() => console.log("[COLLAB EXTENSION] received update", packet.type, packet.payload));
      },
      (err) => errors.next(err),
      () => errors.next("Connection closed")
    );
    const outgoingBuffer = new Subject();
    doc.on("update", (update) => {
      if (disableUpdates || options?.readonly) {
        return;
      }
      outgoingBuffer.next(update);
    });
    outgoingBuffer.pipe(takeUntil(until), bufferTime(0)).subscribe((updates) => {
      if (options?.readonly) {
        return;
      }
      const merged = updates.length > 1 ? yjs.mergeUpdates(updates) : updates[0];
      if (!merged) {
        return;
      }
      bufferToBase64(merged).then((str) => {
        if (str === lastUpdate) {
          return;
        }
        onDebug(() => console.log("[COLLAB EXTENSION] sending update", str));
        commitCollabPacket(
          roomID,
          _class,
          userID ?? cid,
          awareness.getLocalState()?.user?.name,
          CollabPacketType.Update,
          str
        )().catch(console.error);
      }).catch(console.error);
    });
    awareness.on("update", () => {
      if (lastLocalState !== awareness.getLocalState()) {
        lastLocalState = awareness.getLocalState();
        sendAwarenessUpdate();
      }
    });
    onDebug(() => console.log("ClientID", cid));
  };
  const currentUsername = () => {
    return awareness.getLocalState()?.user?.name ?? noUsername;
  };
  const currentUserID = () => {
    return userID ?? cid;
  };
  const state = {
    /**
     * Whether the extension is ready.
     */
    ready: new BehaviorSubject(false),
    /**
     * yjs document.
     */
    doc,
    /**
     * yjs awareness.
     */
    awareness,
    /**
     * Permanent user data.
     */
    pud,
    /**
     * Connected room ID.
     */
    roomID,
    /**
     * yjs client id of the session.
     */
    cid,
    /**
     * Subject for the userID.
     */
    subjectUserID,
    /**
     * Subject for the username.
     */
    subjectUsername,
    /**
     * Subject for errors.
     */
    errors,
    /**
     * Subject to stop the extension.
     */
    until,
    /**
     * Fetch the current username.
     * @returns The current username.
     */
    currentUsername,
    /**
     * Fetch the current userID.
     * @returns The current userID.
     */
    currentUserID,
    /**
     * Fetch the permanent user data.
     * @returns The permanent user data or null if not initialized.
     */
    getPermanentUserData() {
      return pud;
    },
    getConnectedUserCount() {
      return awareness.getStates().size;
    },
    getConnectedUsernames() {
      return Array.from(awareness.getStates().values()).map(
        (s) => s.user?.name ? {
          username: s.user?.name,
          color: s.user?.color
        } : null
      ).filter(Boolean);
    },
    /**
     * Add a new version of the document.
     */
    addVersion() {
      const versions = doc.getArray("versions");
      const prevVersion = versions.length === 0 ? null : versions.get(versions.length - 1);
      const prevSnapshot = prevVersion === null ? yjs.emptySnapshot : yjs.decodeSnapshot(prevVersion?.snapshot);
      const snapshot2 = yjs.snapshot(doc);
      if (prevVersion != null) {
        prevSnapshot.sv.set(prevVersion.clientID, (prevSnapshot.sv.get(prevVersion.clientID) ?? 0) + 1);
      }
      if (!yjs.equalSnapshots(prevSnapshot, snapshot2)) {
        versions.push([
          {
            date: (/* @__PURE__ */ new Date()).getTime(),
            snapshot: yjs.encodeSnapshot(snapshot2),
            clientID: doc.clientID,
            username: currentUsername(),
            userID: currentUserID(),
            id: A.getGloballyUniqueID()
          }
        ]);
      }
    },
    /**
     * Restore a version of the document.
     * @param version VersionData to restore.
     */
    restoreVersion(version) {
      const snap = yjs.decodeSnapshot(version.snapshot);
      const tempdoc = yjs.createDocFromSnapshot(doc, snap);
      const currentStateVector = yjs.encodeStateVector(doc);
      const snapshotStateVector = yjs.encodeStateVector(tempdoc);
      const changesSinceSnapshotUpdate = yjs.encodeStateAsUpdate(doc, snapshotStateVector);
      const um = new yjs.UndoManager([tempdoc.getXmlFragment("prosemirror")]);
      yjs.applyUpdate(tempdoc, changesSinceSnapshotUpdate);
      um.undo();
      const revertChangesSinceSnapshotUpdate = yjs.encodeStateAsUpdate(tempdoc, currentStateVector);
      yjs.applyUpdate(doc, revertChangesSinceSnapshotUpdate, {
        user: {
          id: "revert",
          username: "revert"
        }
      });
    },
    /**
     * Restore a version of the document by index.
     * @param index Index of the version to restore.
     */
    restoreVersionByIndex(index) {
      const versions = doc.getArray("versions");
      const version = versions.get(index);
      if (version) {
        state.restoreVersion(version);
      }
    },
    /**
     * Clear all versions of the document.
     * @returns The number of versions cleared.
     */
    versionCount() {
      return doc.getArray("versions").length;
    },
    /**
     * Returns the latest version of the document.
     * @returns The latest version of the document.
     */
    latestVersion() {
      const versions = doc.getArray("versions");
      return versions.get(versions.length - 1);
    },
    /**
     * Key for the ySyncPlugin.
     */
    ySyncPluginKey,
    /**
     * Extension
     */
    extension: [
      {
        styles: ({ host }) => [
          {
            ".ProseMirror > .ProseMirror-yjs-cursor:first-child": {
              marginTop: "16px"
            },
            ".ProseMirror p:first-child, .ProseMirror h1:first-child, .ProseMirror h2:first-child, .ProseMirror h3:first-child, .ProseMirror h4:first-child, .ProseMirror h5:first-child, .ProseMirror h6:first-child": {
              marginTop: "16px"
            },
            ".ProseMirror-yjs-cursor": {
              position: "relative",
              marginLeft: "-1px",
              marginRight: "-1px",
              borderLeft: "1px solid black",
              borderRight: "1px solid black",
              borderColor: "orange",
              wordBreak: "normal",
              pointerEvents: "none"
            },
            ".ProseMirror-yjs-cursor > div": {
              position: "absolute",
              top: "-1.35em",
              left: "-1px",
              fontSize: "13px",
              padding: "0.1em 0.5em",
              backgroundColor: "rgb(250, 129, 0)",
              fontFamily: "sans-serif",
              fontStyle: "normal",
              fontWeight: "normal",
              lineHeight: "normal",
              userSelect: "none",
              color: "black",
              whiteSpace: "nowrap",
              borderRadius: "5px 5px 5px 0"
            },
            "[ychange_type]": {
              position: "relative"
            },
            ".ychange-hover": {
              display: "none"
            },
            ":hover > .ychange-hover": {
              display: "inline",
              position: "absolute",
              top: "-14px",
              left: "0",
              fontSize: "12px",
              padding: "0 2px",
              borderRadius: "3px 3px 0 0",
              color: "#fdfdfe",
              userSelect: "none",
              wordBreak: "normal"
            },
            "ychange[ychange_type='removed'], p[ychange_type='removed']": {
              textDecoration: "line-through"
            },
            ":not(ychange)[ychange_type='removed']": {
              backgroundColor: "#ff5a56",
              color: "inherit !important"
            },
            "img[ychange_type='removed']": {
              padding: "2px"
            }
          }
        ],
        extensions: [
          Extension.create({
            name: "Collab",
            onCreate() {
              onDebug(() => console.log("[COLLAB EXTENSION] created"));
              registerCollab().then(() => state.ready.next(true)).catch(console.error);
            },
            onDestroy() {
              onDebug(() => console.log("[COLLAB EXTENSION] destroyed"));
              disableUpdates = true;
              subjectUserID.complete();
              subjectUsername.complete();
              until.next(true);
              until.complete();
            },
            addProseMirrorPlugins: () => {
              return [
                ySyncPlugin(text, {
                  // * As the PermanentUserData is only allowed to initialize after the document is populated
                  // * we use this workaround to allow the pud to not be present at plugin init.
                  permanentUserData: new Proxy(
                    {},
                    {
                      get(target, prop) {
                        return pud?.[prop] ?? (() => null);
                      }
                    }
                  ),
                  // * Workaround to redirect the color mapping to our stable color selection function.
                  colorMapping: {
                    has: (user) => true,
                    get: (user) => {
                      const color = getStableColorByUsername(user ?? "");
                      return { light: color, dark: color };
                    }
                  },
                  colors: colorPallet.map((c) => ({ light: c, dark: c }))
                }),
                yUndoPlugin(),
                yCursorPlugin(awareness),
                ...options?.enableUndoKeyMap ? [
                  keymap({
                    "Mod-z": undo,
                    "Mod-y": redo,
                    "Mod-Shift-z": redo
                  })
                ] : []
              ];
            }
          }),
          yChangeMark
        ]
      }
    ]
  };
  return state;
}
export {
  createCollabExtension,
  createCollabExtensionByClass,
  getDefaultColorPallet,
  setDefaultColorPallet
};
